//this file is Dashboard.jsx

import React, { Suspense, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AdminLogin from 'src/components/dashboard/Adminlogin';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { CONSTANTS as Q_CONSTANTS } from 'src/components/questionnaire/Constants';
import {
  basePath as customFormBasePath,
  CONSTANTS as CUSTOMFORM_CONSTANTS
} from 'src/components/custom-form/Constants';
import BackdropLoader from 'src/components/UI/loader/BackdropLoader';
import { ThemeContext, UserContext } from 'src/store/ContextStore';
import CustomForm from 'src/components/custom-form/CustomForm';
import PatientPanel from '../patient/PatientPanel';
import StudentRegister from './StudentRegister';
import TrialSignup from 'src/components/trial/TrialSignup';
import TrialVerify from 'src/components/trial/TrialVerify';
import { StyledEngineProvider, createTheme } from '@mui/material/styles';
import LocalisedMuiTheme from 'src/i18n/LocalisedMuiTheme';
import axios from 'src/axios';
import useDocumentFavicon from 'src/hooks/useDocumentFavicon';
import { forgetRetiredBrowserState } from 'src/util/forgetRetiredBrowserState';
import { green } from '@mui/material/colors';
import {
  BRAND_COLOURS,
  CLINICAL_COLOURS,
  tileColoursFor,
  changedColoursFor
} from 'src/theme/palette';

// lazy loading for app and admin modules
const AdminPanel = React.lazy(() => import('src/components/admin/AdminPanel'));
const Questionnaire = React.lazy(() =>
  import('src/components/questionnaire/Questionnaire')
);
const GenerateLink = React.lazy(() =>
  import('src/components/questionnaire/generate-link/GenerateLink')
);
const CustomFormGenerateLink = React.lazy(() =>
  import('src/components/custom-form/generate-link/GenerateLink')
);
const ResetPassword = React.lazy(() =>
  import('src/components/dashboard/ResetPassword')
);
const ErrorPage = React.lazy(() =>
  import('src/components/dashboard/ErrorPage')
);
const SapioError = React.lazy(() =>
  import('src/components/dashboard/SapioError')
);
const SapioCallback = React.lazy(() =>
  import('src/components/dashboard/SapioCallback')
);
const SmartLaunchInit = React.lazy(() =>
  import('src/components/smart/SmartLaunchInit')
);
const SmartCallback = React.lazy(() =>
  import('src/components/smart/SmartCallback')
);
// Security monitor
const Lockdown = React.lazy(() => import('src/components/lockdown/Lockdown'));

const Dashboard = () => {
  const [user, setUser] = React.useState(null);

  // const clearSession = async () => {
  //   //await axios.get(APICONSTANTS.logout);
  //   history.push(CONSTANTS.signinPath);
  //   setUser(null);
  //   localStorage.setItem('2FAloggedin', 'false');
  // };

  // useEffect(() => {
  //   const isUser2FAloggedin = localStorage.getItem('2FAloggedin');

  //   if (isUser2FAloggedin !== 'true') {
  //     clearSession();
  //   }
  // }, []);

  const userProviderValue = React.useMemo(
    () => ({ user, setUser }),
    [user, setUser]
  );

  const [theme, setTheme] = useState(CONSTANTS.customTheme);

  React.useMemo(() => {
    axios
      .get(APICONSTANTS.getCustomTheme)
      .then((res) => {
        if (res.data) setTheme(res.data);
      })
      .catch((/*err*/) => {
        // setTheme(CONSTANTS.customTheme);
      });
  }, [user]);

  // Swap the document favicon to the custom one when the theme loads —
  // but only for patient flows (FHQ / custom-form access pages, where the
  // user is unauthenticated and we want the issuing clinic's branding).
  // Logged-in users always see the bundled TrakGene favicon for consistent
  // tab-strip identity across User Groups.
  useDocumentFavicon(user ? null : theme?.faviconURL);

  // The retired Calendly integration (TGCLOUD-1390) left three keys in
  // localStorage. One, 'code', held whatever query string the app was opened
  // with, link tokens included. Clear them once on load. This can be removed
  // after a couple of releases.
  React.useEffect(() => {
    forgetRetiredBrowserState();
  }, []);

  const muiCustomTheme = React.useMemo(
    () =>
      createTheme({
        dateFormat: 'MM/DD/YYYY',
        typography: {
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif"
        },
        palette: {
          mode: theme.appearance.value === 'light' ? 'light' : 'dark',
          secondary: {
            main: theme.buttonStyle ?? '#f50057',
            contrastText: theme.buttonText ?? '#FFFFFF'
          },
          primary: {
            main: theme.buttonStyle ?? '#42a5f5',
            contrastText: theme.buttonText ?? '#FFFFFF'
          },
          // TGCLOUD-1869: the named brand and clinical colours
          // (src/theme/palette.js), fixed for every deployment, unlike the
          // colours above, which come from the group's theme. The home tiles
          // follow the group's accent, as the nav rail does (TGCLOUD-1896).
          brand: BRAND_COLOURS,
          clinical: CLINICAL_COLOURS,
          tile: tileColoursFor(theme.accent),
          // TGCLOUD-1902: an unsaved change on a clinician's edit screen.
          changed: changedColoursFor(
            theme.appearance.value === 'light' ? 'light' : 'dark'
          )
        },
        components: {
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: theme.highlighter || green[500],
                    border: `2px solid ${theme.highlighter}`
                  }
              }
            }
          },
          MuiSelect: {
            styleOverrides: {
              root: {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.highlighter ?? green[500]
                }
              }
            }
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                backgroundColor:
                  theme.appearance?.value === 'light'
                    ? '#ffffff'
                    : theme.appearance?.shade ?? 'inherit'
              }
            }
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                backgroundColor: theme.appearance?.color ?? '#ffffff'
              }
            }
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                backgroundColor: `${theme.buttonStyle ?? '#42a5f5'} !important`,
                color: `${theme.buttonText ?? '#000'} !important`
              }
            }
          },
          MuiTab: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: `${
                    theme.accent + '1a' ?? '#ffffff'
                  } !important`
                }
              }
            }
          }
        },
        // Auth-screen design tokens (radius / sizing) — a scoped custom key so
        // the login/MFA/reset shared layout is theme-driven without changing the
        // app-wide MUI `shape.borderRadius`. Brand COLOURS still come from the
        // palette above; these are neutral layout constants only.
        auth: {
          cardRadius: 16,
          cardMaxWidth: 460,
          narrowMaxWidth: 420,
          logoMaxHeight: 56
        },
        userTheme: theme
      }),
    [theme]
  );

  React.useMemo(() => {
    document.body.id = theme.appearance.value;
  }, [theme]);

  return (
    <UserContext.Provider value={userProviderValue}>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <StyledEngineProvider injectFirst>
          {/* MUI's own text in the selected language (TGCLOUD-1893). */}
          <LocalisedMuiTheme theme={muiCustomTheme}>
            <Routes>
              {/* Custom form generate link */}
              <Route
                path={CUSTOMFORM_CONSTANTS.generateLinkPath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <CustomFormGenerateLink />
                  </Suspense>
                }
              />

              <Route
                path={Q_CONSTANTS.generateLinkPath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <GenerateLink />
                  </Suspense>
                }
              />
              {/* Descendant router (UserForm) — needs a splat so nested
                  step paths (personal-details, family-details, …) match. */}
              <Route
                path={`${Q_CONSTANTS.formPath}/*`}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <Questionnaire />
                  </Suspense>
                }
              />
              <Route
                path={`${customFormBasePath}/*`}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <CustomForm />
                  </Suspense>
                }
              />

              <Route
                path={CONSTANTS.signinPath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <AdminLogin />
                  </Suspense>
                }
              />
              <Route
                path={CONSTANTS.resetPasswordPath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <AdminLogin />
                  </Suspense>
                }
              />

              <Route
                path={CONSTANTS.studentRegister}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <StudentRegister />
                  </Suspense>
                }
              />

              {/* Trial sign-up (TGCLOUD-1844). The pages exist everywhere;
                  the API behind them only on the hosted instance, so elsewhere
                  they say "not available". */}
              <Route
                path={CONSTANTS.trialSignupPath}
                element={<TrialSignup />}
              />
              <Route
                path={CONSTANTS.trialVerifyPath}
                element={<TrialVerify />}
              />

              <Route
                path={CONSTANTS.resetPasswordLinkPath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <ResetPassword />
                  </Suspense>
                }
              />

              <Route
                path={CONSTANTS.errorPagePath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <ErrorPage />
                  </Suspense>
                }
              />

              <Route
                path={CONSTANTS.sapioErrorPath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <SapioError />
                  </Suspense>
                }
              />

              <Route
                path={CONSTANTS.sapioCallbackPath}
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <SapioCallback />
                  </Suspense>
                }
              />

              <Route
                path="/smart/launch"
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <SmartLaunchInit />
                  </Suspense>
                }
              />

              <Route
                path="/smart/callback"
                element={
                  <Suspense fallback={<BackdropLoader loading={true} />}>
                    <SmartCallback />
                  </Suspense>
                }
              />

              {/* {user?.userType === CONSTANTS.userTypes.ADMIN && (
                <Route
                  path={CONSTANTS.lockdownPath}
                  element={
                    <Suspense fallback={<BackdropLoader loading={true} />}>
                      <Lockdown />
                    </Suspense>
                  }
                />
              )} */}

              {/* Descendant routers (AdminPanel/PatientPanel -> MainContent/
                  PatientMain) — need a splat to match their nested routes. */}
              {user?.userType === CONSTANTS.userTypes.PATIENT ? (
                <Route
                  path={`${CONSTANTS.patientPath}/*`}
                  element={
                    <Suspense fallback={<BackdropLoader loading={true} />}>
                      <PatientPanel />
                    </Suspense>
                  }
                />
              ) : (
                <Route
                  path={`${CONSTANTS.adminPath}/*`}
                  element={
                    <Suspense fallback={<BackdropLoader loading={true} />}>
                      <AdminPanel />
                    </Suspense>
                  }
                />
              )}

              <Route
                path="*"
                element={
                  <Navigate
                    to={
                      user?.userType === CONSTANTS.userTypes.PATIENT
                        ? CONSTANTS.patientPath
                        : CONSTANTS.adminPath
                    }
                    replace
                  />
                }
              />
            </Routes>
          </LocalisedMuiTheme>
        </StyledEngineProvider>
      </ThemeContext.Provider>
    </UserContext.Provider>
  );
};

export default Dashboard;
